import type { PropsWithChildren, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useOutsideClick } from '../../hooks/useOutsideClick';

const ModalBackground = styled.div<{ show: boolean }>(
  ({ show, theme }) => css`
    z-index: ${theme.zIndexes.modal};
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    background: ${theme.colors.main.overlay};
    transition: opacity 0.35s ease;
    opacity: ${show ? 1 : 0};
  `
);

const ModalContainer = styled.div(
  ({ theme }) => css`
    position: fixed;
    background: ${theme.colors.white};
    width: 90%;
    max-height: 88%;
    overflow-y: auto;
    top: 6%;
    left: 50%;
    transform: translate(-50%, 0);
    border-radius: ${theme.borderRadii.default};
    padding: 60px;
    max-width: 700px;

    .modal-xl & {
      max-width: 1500px;
    }

    .modal-lg & {
      max-width: 1200px;
    }

    @media (max-width: ${theme.breakpoints.max.sm}) {
      padding: 30px;
      width: 95%;
    }
  `
);

const CloseIcon = styled.button(
  ({ theme }) => css`
    position: absolute;
    top: 24px;
    right: 24px;
    padding: 0;
    line-height: 0px;
    background: none;
    border: 0;

    span {
      font-size: 36px !important;
    }

    @media (hover: hover) {
      &:hover {
        cursor: pointer;
        color: ${theme.colors.main.accent};
      }
    }

    @media (max-width: ${theme.breakpoints.max.sm}) {
      top: 15px;
      right: 15px;
    }
  `
);

const ModalContent = styled.div(
  ({ theme }) => css`
    font-size: ${theme.fonts.text};
  `
);

type ModalProps = {
  handleClose: () => void;
  isModalVisible: boolean;
  content?: React.ReactNode;
  className?: React.HTMLAttributes<HTMLDivElement>['className'];
};

const Modal = ({
  handleClose, // renderProp fn returns true or false
  isModalVisible, // boolean - visible/invisible
  content,
  className,
  children
}: PropsWithChildren<ModalProps>) => {
  const [isOpenTransition, setIsOpenTransition] = useState(false);

  useEffect(() => {
    if (isModalVisible === true) setIsOpenTransition(true);
  }, [isModalVisible]);

  const closeModal = () => {
    setIsOpenTransition(false);
    setTimeout(() => {
      handleClose();
    }, 250);
  };

  useEscapeKey(closeModal);

  const ref: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  useOutsideClick(isModalVisible, closeModal, ref);

  return (
    <>
      {isModalVisible && (
        <ModalBackground show={isOpenTransition} className={className}>
          <ModalContainer ref={ref}>
            <CloseIcon onClick={closeModal}>
              <span className="material-symbols-rounded">close</span>
            </CloseIcon>
            <ModalContent>{content || children}</ModalContent>
          </ModalContainer>
        </ModalBackground>
      )}
    </>
  );
};

export { Modal };
